"use client";

import { AttributeGroupType, AttributeType, ResolvedAttributeType } from "@/app/types/attribute";
import { Box, Button, colors, Typography } from "@mui/material";
import { dayjs } from "@v2/lib/dayjs";
import { Check, ChevronDown, Ellipsis, Minus } from "lucide-react";
import { ReactNode, useState } from "react";
import { usePdpVariant } from "./PdpVariantContext";

type Row = Pick<ResolvedAttributeType, "title" | "type" | "value"> & { group_id: number | null };

/** A group with its icon drawn on the server, so the icon set stays out of the bundle. */
export type PdpSpecificationGroup = AttributeGroupType & { iconNode: ReactNode };

type Section = { key: string; title: string; icon: ReactNode; rows: Row[] };

const OTHER_TITLE = "سایر مشخصات";

/** On phones a long table is cut here until the visitor asks for the rest. */
const MOBILE_COLLAPSED_HEIGHT = 440;
const MOBILE_COLLAPSE_FROM_ROWS = 9;

function ShownValue({ row }: { row: Row }) {
    if (row.type === "boolean" && (row.value === "1" || row.value === "0")) {
        const yes = row.value === "1";
        return (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                {yes
                    ? <Check size={16} strokeWidth={2.25} color={colors.green[600]} />
                    : <Minus size={16} strokeWidth={2.25} color={colors.grey[500]} />}
                {yes ? "بله" : "خیر"}
            </Box>
        );
    }
    // Dates are stored Gregorian; several of them arrive joined.
    if (row.type === "date") {
        return row.value.split(", ").map(value => {
            const date = dayjs(value, "YYYY-MM-DD");
            return date.isValid() ? date.calendar("jalali").format("YYYY/MM/DD") : value;
        }).join("، ");
    }
    if (row.type === "multiple_selection") return row.value.split(", ").join("، ");
    return row.value;
}

/**
 * Groups in their page order, then everything without a known group under
 * "other specifications". Rows keep the order the API sent them in.
 */
function toSections(rows: Row[], groups: PdpSpecificationGroup[]): Section[] {
    const sections: Section[] = groups
        .map(group => ({
            key: String(group.id),
            title: group.title,
            icon: group.iconNode,
            rows: rows.filter(row => row.group_id === group.id),
        }))
        .filter(section => section.rows.length);
    const other = rows.filter(row => !groups.some(group => group.id === row.group_id));
    if (other.length) {
        sections.push({ key: "other", title: OTHER_TITLE, icon: <Ellipsis size={20} strokeWidth={1.75} color={colors.grey[800]} />, rows: other });
    }
    return sections;
}

function SpecRows({ rows }: { rows: Row[] }) {
    return (
        <Box component="dl" sx={{ m: 0 }}>
            {rows.map((row, index) => (
                <Box key={index} sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: { xs: 1.25, md: 1.5 },
                    borderTop: index === 0 ? 'none' : '1px solid',
                    borderColor: colors.grey[200],
                }}>
                    <Typography component="dt" sx={{ fontSize: { xs: 13, md: 14 }, color: colors.grey[600], flexShrink: 0, maxWidth: '48%' }}>
                        {row.title}
                    </Typography>
                    <Typography component="dd" sx={{ m: 0, display: 'flex', justifyContent: 'flex-end', fontSize: { xs: 13, md: 14 }, fontWeight: 500, color: colors.grey[900], textAlign: 'end', overflowWrap: 'anywhere' }}>
                        <ShownValue row={row} />
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

/**
 * The specification table of the model on show. Each model carries its own
 * rows, so picking another variant switches the table without a request. The
 * server renders the default model; a pid link switches it before paint.
 *
 * Phones get one column, a group heading over its rows, and a cut-off with a
 * "show all" button. From md up the group title sits in its own column beside
 * the rows, and nothing is cut.
 */
export default function PdpSpecifications({ groups, fallback }: {
    groups: PdpSpecificationGroup[];
    /** The site product rows, for a response cached before models carried their own. */
    fallback: AttributeType[];
}) {
    const { showProduct } = usePdpVariant();
    const [expanded, setExpanded] = useState(false);

    const rows: Row[] = (showProduct.attributes ?? fallback.map(attribute => ({ ...attribute, group_id: null })))
        // A row without a value says nothing.
        .filter(row => typeof row.value === "string" && row.value.trim() !== "");
    if (!rows.length) return null;

    const sections = toSections(rows, groups);
    // Without any group there is nothing to name, so the table stays a single plain list.
    const withHeadings = sections.some(section => section.key !== "other");
    const collapsible = !expanded && rows.length >= MOBILE_COLLAPSE_FROM_ROWS;

    return (
        <Box component="section" sx={{ bgcolor: '#fff', px: { xs: 2, md: 3 }, pt: 2, pb: { xs: 2, md: 1 }, mt: '4px', borderRadius: 1 }}>
            {/* Plain and heavier than the group headings, so the icon tiles below stay the only icons. */}
            <Typography sx={{ fontSize: { xs: 17, md: 19 }, fontWeight: 700, color: colors.grey[900], pt: 1, pb: { xs: 2, md: 2.5 } }}>
                مشخصات
            </Typography>

            <Box sx={{
                position: 'relative',
                overflow: 'hidden',
                maxHeight: { xs: collapsible ? MOBILE_COLLAPSED_HEIGHT : 'none', md: 'none' },
                // Fades the cut rows out instead of slicing a line in half.
                '&::after': {
                    content: '""',
                    display: { xs: collapsible ? 'block' : 'none', md: 'none' },
                    position: 'absolute',
                    insetInline: 0,
                    bottom: 0,
                    height: 96,
                    pointerEvents: 'none',
                    background: 'linear-gradient(to bottom, rgba(255,255,255,0), #fff)',
                },
            }}>
                {withHeadings ? sections.map((section, index) => (
                    <Box key={section.key} sx={{
                        display: { xs: 'block', md: 'flex' },
                        gap: 4,
                        pt: { xs: index === 0 ? 0 : 2.5, md: 2 },
                        pb: { xs: 0, md: 1 },
                        borderTop: { xs: 'none', md: '1px solid' },
                        borderColor: { md: colors.grey[300] },
                    }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            width: { md: 220 },
                            flexShrink: 0,
                            alignSelf: 'flex-start',
                            pt: { md: 1.25 },
                            pb: { xs: 0.5, md: 0 },
                        }}>
                            <Box sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 2,
                                bgcolor: colors.grey[100],
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                {section.icon}
                            </Box>
                            <Typography sx={{ fontSize: { xs: 14, md: 15 }, fontWeight: 600, color: colors.grey[900] }}>
                                {section.title}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <SpecRows rows={section.rows} />
                        </Box>
                    </Box>
                )) : (
                    <SpecRows rows={rows} />
                )}
            </Box>

            {collapsible && (
                <Button color="inherit" variant="outlined" fullWidth onClick={() => setExpanded(true)}
                    endIcon={<ChevronDown size={18} color={colors.grey[800]} />}
                    sx={{ display: { xs: 'flex', md: 'none' }, height: 44, mt: 1.5, borderColor: colors.grey[300], color: colors.grey[800] }}>
                    نمایش همه مشخصات
                </Button>
            )}
        </Box>
    );
}
